import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Inject,
  OnDestroy,
  OnInit,
  Renderer2,
} from '@angular/core';
// tslint:disable-next-line:nx-enforce-module-boundaries
import { Meta, Title } from '@angular/platform-browser';
import { DocumentVisibilityService } from '@epicuro-next/ataraxia/document-visibility';
import { RecommendationsPanels } from '@epicuro-next/models/recommendation-panels';
import { detectBrowser } from '@epicuro-next/platform/browser-detection';
import {
  ConfigurationsService,
  selectPreventVisualSearchInEdge,
} from '@epicuro-next/platform/configuration';
// tslint:disable-next-line:nx-enforce-module-boundaries
// tslint:disable-next-line:nx-enforce-module-boundaries
import {
  EnvironmentSettings,
  ENVIRONMENT_SETTINGS,
} from '@epicuro-next/platform/environment';
import { IntercomService } from '@epicuro-next/platform/intercom';
import { ScriptsInjectorService } from '@epicuro-next/platform/third-party-scripts';
// tslint:disable-next-line:nx-enforce-module-boundaries
import {
  AbstractTrackClient,
  GCRService,
} from '@epicuro-next/platform/tracking';
import { selectUrl } from '@epicuro-next/state/router-store';
// tslint:disable-next-line:nx-enforce-module-boundaries
import { stopSignalrConnection } from '@epicuro-next/state/signalr-state';
import { Store } from '@ngrx/store';
import * as Sentry from '@sentry/angular';
import {
  BehaviorSubject,
  combineLatest,
  filter,
  map,
  Observable,
  of,
  switchMap,
} from 'rxjs';

import { environment } from '../environments/environment';

import {
  FiltersConfigurationKeys,
  NAMESPACE_SPOTLIGHT,
} from './configurations';
import { CookieConsentService } from './cookie-consent.service';
import { VersionCheckerService } from './update-checker/version-checker.service';
import { NavigationError, Router } from '@angular/router';

@Component({
  selector: 'epc-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class AppComponent implements OnInit, OnDestroy {
  public readonly spotlightRecommendationPanel =
    RecommendationsPanels.Spotlight;

  public recommendationPanelHasItems$ = new BehaviorSubject(true);

  public recommendationPanelHasNoItems$ =
    this.recommendationPanelHasItems$.pipe(
      map((recommendationPanelHasItems) => !recommendationPanelHasItems),
    );

  public showSpotlightNamespace = NAMESPACE_SPOTLIGHT;
  public showSpotLightFeatureFlag = FiltersConfigurationKeys.ShowSpotlight;
  public readonly preventVisualSearchInEdge$ = this.store.select(
    selectPreventVisualSearchInEdge,
  );

  public shouldShowSpotlightPanel$: Observable<boolean> = combineLatest([
    this.configsService.getConfigValue({
      namespace: NAMESPACE_SPOTLIGHT,
      featureName: FiltersConfigurationKeys.HideSpotlightPanel,
      defaultValue: ['site-selector'],
    }),
    this.store.select(selectUrl),
  ]).pipe(
    switchMap(([blacklist, url]: [string[], string]) => {
      return of(
        blacklist?.every((partialUrl) => {
          return !url?.includes(partialUrl);
        }),
      );
    }),
  );

  constructor(
    @Inject(AbstractTrackClient) trackingClient: AbstractTrackClient,
    private readonly store: Store,
    @Inject(ENVIRONMENT_SETTINGS)
    private environmentSettings: EnvironmentSettings,
    private titleService: Title,
    private metaService: Meta,
    public intercomService: IntercomService,
    cookieConsentService: CookieConsentService,
    documentVisibility: DocumentVisibilityService,
    versionService: VersionCheckerService,
    private readonly renderer: Renderer2,
    private readonly elementRef: ElementRef,
    scriptsInjector: ScriptsInjectorService,
    gcrService: GCRService,
    private configsService: ConfigurationsService,
    private router: Router,
  ) {
    if (environment.production) {
      documentVisibility.documentVisible$
        .pipe(filter(Boolean))
        .subscribe(() => versionService.checkForNewVersion());
    }

    this.clearServiceWorkers();

    this.setTitle();
    this.setMeta();
    cookieConsentService.setCookieConsent();

    trackingClient.startTracking();
    gcrService.startTracking();
    intercomService.boot();
    scriptsInjector.injectScripts();
  }

  public ngOnInit(): void {
    if (detectBrowser('isEdge')) {
      this.preventVisualSearchInEdge$.subscribe((preventVisualSearchInEdge) => {
        if (preventVisualSearchInEdge) {
          this.applyCssTrickToPreventVisualSearchInEdgeOnImages();
        }
      });
    }
    this.promptOnLoadingChunkErrors();
  }

  private applyCssTrickToPreventVisualSearchInEdgeOnImages() {
    const edgeStyles = this.renderer.createElement('style');
    const preventVisualSearchInEdgeCssStyleTrick =
      'img { pointer-events: none; }';
    this.renderer.setProperty(
      edgeStyles,
      'innerHTML',
      preventVisualSearchInEdgeCssStyleTrick,
    );

    this.renderer.appendChild(
      this.elementRef.nativeElement.ownerDocument.head,
      edgeStyles,
    );
  }

  private setTitle() {
    if (this.environmentSettings.applicationTitle) {
      this.titleService.setTitle(this.environmentSettings.applicationTitle);
    }
  }

  private setMeta() {
    this.metaService.addTag({
      property: 'og:url',
      content: window.location.origin,
    });
  }

  public ngOnDestroy(): void {
    this.store.dispatch(stopSignalrConnection());
  }

  public clearServiceWorkers() {
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker
        .getRegistrations()
        .then((registrations) => {
          // returns installed service workers
          if (registrations.length) {
            for (const registration of registrations) {
              registration.unregister().catch((error) => console.error(error));
            }
          }
        })
        .catch((error) => console.error(error));
    }
  }
  private promptOnLoadingChunkErrors() {
    this.router.events
      .pipe(
        filter((evt) => evt instanceof NavigationError),
        map((evt) => evt as NavigationError),
      )
      .subscribe((evt) => {
        if (evt.error instanceof Error && evt.error.name == 'ChunkLoadError') {
          const error = new Error(`ChunkLoadError: prompt user to reload`);
          Sentry.captureException(error);
          alert(
            $localize`:@@loadChunkAlertText:Something went wrong. Page will be refreshed automatically`,
          );
          window.location.assign(evt.url);
        }
      });
  }
}
